import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Target, TrendingUp, Zap, ArrowRight, CheckCircle } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-2.5 font-bold text-xl sm:text-2xl text-emerald-600">
              <span className="bg-emerald-600 text-white p-2 rounded text-lg sm:text-xl leading-none">🏏</span>
              <span className="text-slate-900 tracking-tight">Legacy Cricket Academy</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/auth")}
                className="flex items-center h-10 px-3 text-slate-600 hover:text-slate-900"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate("/register")}
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto text-center">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5 max-w-3xl mx-auto leading-tight tracking-tight [text-wrap:balance]"
            data-testid="hero-headline"
          >
            High-Performance Cricket Coaching for Kids
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mb-4 max-w-2xl mx-auto leading-relaxed">
            Real-game coaching that builds athleticism, technique, and winning mindset.
            From fundamentals to match-ready performance.
          </p>
          <div
            className="flex flex-wrap justify-center gap-3 mb-4"
            data-testid="hero-audience"
          >
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 font-semibold text-sm sm:text-base">
              Boys &amp; Girls
            </span>
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm sm:text-base">
              Ages 5+
            </span>
          </div>
          <p
            className="text-sm sm:text-base text-slate-500 mb-6 max-w-xl mx-auto"
            data-testid="hero-location"
          >
            Prosper / Dallas, TX
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all"
              data-testid="register-cta-button"
            >
              Register Your Kid
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/register")}
              className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-lg px-8 py-6 rounded-full"
              data-testid="trial-cta-button"
            >
              Join Trial Session
            </Button>
          </div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-12">
            Our Training Pillars
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Pillar 1: Batting */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Batting Skills</h3>
              <p className="text-slate-600 mb-4">
                Master timing, shot selection, and power hitting. Build confidence at the crease with proven techniques.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Footwork & stance fundamentals</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Shot selection & placement</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Power generation & timing</span>
                </li>
              </ul>
            </div>

            {/* Pillar 2: Bowling & Fielding */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <TrendingUp className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Bowling & Fielding</h3>
              <p className="text-slate-600 mb-4">
                Develop consistency, accuracy, and agility. Learn to control the game with precision bowling and sharp fielding.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Bowling action & line control</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Fielding positions & techniques</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Catching & throwing accuracy</span>
                </li>
              </ul>
            </div>

            {/* Pillar 3: Fitness */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <Zap className="h-7 w-7 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Fitness & Athleticism</h3>
              <p className="text-slate-600 mb-4">
                Build speed, strength, and coordination. Athletic development for peak performance on the field.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span>Speed & agility training</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span>Strength & conditioning</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span>Coordination & balance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Age Groups Section — chronological brackets, cricket-style.
          These are the structural buckets that scale into tournament play
          (PR-X3 match_lineups). Skill is shown as a separate dimension below. */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8"
        data-testid="age-groups-section"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-3">
            Age Groups
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto px-4">
            Cricket-style age brackets for structured development and future tournament play.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {/* U7 */}
            <div
              className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100 hover:shadow-md transition-shadow text-center"
              data-testid="age-group-u7"
            >
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">U7</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Ages 5–7</h3>
              <p className="text-slate-600 text-sm">
                First introduction to cricket through fun games, coordination drills, and fundamentals.
              </p>
            </div>

            {/* U11 */}
            <div
              className="bg-blue-50 rounded-2xl p-6 border border-blue-100 hover:shadow-md transition-shadow text-center"
              data-testid="age-group-u11"
            >
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">U11</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Ages 8–10</h3>
              <p className="text-slate-600 text-sm">
                Build solid batting, bowling, and fielding technique with structured age-appropriate sessions.
              </p>
            </div>

            {/* U13 */}
            <div
              className="bg-purple-50 rounded-2xl p-6 border border-purple-100 hover:shadow-md transition-shadow text-center"
              data-testid="age-group-u13"
            >
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">U13</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Ages 11–13</h3>
              <p className="text-slate-600 text-sm">
                Refine technique, game awareness, and tactical understanding. Match-scenario practice begins.
              </p>
            </div>

            {/* U15+ */}
            <div
              className="bg-orange-50 rounded-2xl p-6 border border-orange-100 hover:shadow-md transition-shadow text-center"
              data-testid="age-group-u15plus"
            >
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">U15+</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Ages 14+</h3>
              <p className="text-slate-600 text-sm">
                Competitive training, match performance, advanced techniques, and tournament preparation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skill Levels Section — assessment-based, independent of age.
          Mirrors the Beginner | Intermediate | Advanced enum shipped in PR-X1
          (shared/schema.ts batches.skill_level). */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8 bg-white"
        data-testid="skill-levels-section"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-3">
            Skill Levels
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto px-4">
            Within each age group, players are placed by coach assessment — not by age alone.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {/* Beginner */}
            <div
              className="bg-white rounded-2xl p-7 sm:p-8 border-2 border-emerald-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid="skill-level-beginner"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold mb-4">
                <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                Beginner
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Starting Out</h3>
              <p className="text-slate-600">
                New to cricket. Focus on grip, stance, hand-eye coordination, and love for the game through fun activities.
              </p>
            </div>

            {/* Intermediate */}
            <div
              className="bg-white rounded-2xl p-7 sm:p-8 border-2 border-blue-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid="skill-level-intermediate"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold mb-4">
                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                Intermediate
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Building Technique</h3>
              <p className="text-slate-600">
                Comfortable with basics. Refining technique, consistency, shot selection, bowling control, and game awareness.
              </p>
            </div>

            {/* Advanced */}
            <div
              className="bg-white rounded-2xl p-7 sm:p-8 border-2 border-orange-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid="skill-level-advanced"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-sm font-semibold mb-4">
                <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
                Advanced
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Match-Ready</h3>
              <p className="text-slate-600">
                Performance training, match scenarios, power hitting, fast bowling mechanics, fitness, and competitive mindset.
              </p>
            </div>
          </div>
          <p
            className="text-center text-sm text-slate-500 italic mt-8 max-w-2xl mx-auto px-4"
            data-testid="skill-assessment-note"
          >
            Skill grouping is based on coach assessment, not just age. Players may move across levels based on development and readiness.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2.5 font-bold text-xl sm:text-2xl mb-4">
            <span className="bg-emerald-600 text-white p-2 rounded text-lg sm:text-xl leading-none">🏏</span>
            <span className="text-white">Legacy Cricket Academy</span>
          </div>
          <p className="text-slate-400 mb-4">
            Building champions, one session at a time.
          </p>
          <p className="text-slate-500 text-sm">
            © 2025 Legacy Cricket Academy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
